import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import classNames from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import { useNavigate } from 'react-router-dom';
import {
  getQuestionnaireForCustomer,
} from '../../../../services/questionnaire/questionnaireService';
import styles from './QuestionnaireTable.module.css';
import threeDotsLogo from '../../../../assets/images/threeDots.png';
import APIError from '../../../../services/APIError';
import QuestionnaireContext from '../../../../context/questionnaire-context';
import { convertDate, getQuestionnaireName } from './util';

const questionnaires = ['KYC_FINANCIAL_ADVISORY', 'KYC_WEALTH_MANAGEMENT',
  'MIFID_CLIENT_CATEGORISATION', 'MIFID_APPROPRIATENESS', 'MIFID_SUITABILITY'];

type QuestionnaireTableProps = {
  customerId: number;
  customerQuestionnairesRequired: string[];
}

function QuestionnaireTable(props: QuestionnaireTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const questionnaireCtx = useContext(QuestionnaireContext);
  const [showOptionsType, setShowOptionsType] = useState<string>();
  const [showHistory, setShowHistory] = useState(false);


  useEffect(() => {
    const getQuestionnaires = async () => {
      try {
        const response = await getQuestionnaireForCustomer({ id: props.customerId });
        questionnaireCtx.setQuestionnaires(response);
      } catch (err) {
        if (err instanceof APIError) {
          console.log(`Failed to retrieve questionnaires: ${err.errorBody}`);
        } else {
          console.log(err);
        }
      }
    };

    getQuestionnaires();
  }, [props.customerId]);

  useEffect(() => {
    setShowHistory(questionnaireCtx.questionnaires.length !== 0);
  }, [questionnaireCtx.questionnaires]);

  const findFilledQuestionnaireType = (questionnaireType: string) => questionnaireCtx.questionnaires
    .sort((a, b) => b.createTimestamp.localeCompare(a.createTimestamp))
    .find(questionnaire => questionnaire.questionnaireType === questionnaireType);

  const isFilledQuestionnairesHaveType = (questionnaireType: string) =>
    questionnaireCtx.questionnaires.some(questionnaire =>
      questionnaire.questionnaireType === questionnaireType,
    );

  const showCreateTimestamp = (questionnaireType: string) => {
    const questionnaire = findFilledQuestionnaireType(questionnaireType);
    return questionnaire
      ? <div>{convertDate(questionnaire.createTimestamp)}</div>
      : <div>-</div>;
  };

  const showExpirationWarning = (expirationDate: string): boolean => {
    const date = new Date(expirationDate);
    return date.valueOf() <= Date.now();
  };

  const showExpirationDate = (questionnaireType: string) => {
    const questionnaire = findFilledQuestionnaireType(questionnaireType);
    return questionnaire
      ? <div
        className={classNames((showExpirationWarning(questionnaire.expirationDate) &&
            props.customerQuestionnairesRequired.includes(questionnaireType)) &&
          styles.expirationWarning)}
      >
        {convertDate(questionnaire.expirationDate)}
      </div>
      : <div>-</div>;
  };

  const isChecked = (item: string, checklist: string[]) => checklist.includes(item)
    ? <div className={styles.checked} />
    : <div className={styles.unchecked} />;

  const handleHistoryClick = () => {
    navigate('/customer/history');
  };

  const handleViewClick = (questionnaire: string) => {
    const lastFound = findFilledQuestionnaireType(questionnaire);
    questionnaireCtx.setQuestionnaire(lastFound);
    navigate('/customer/questionnaire');
  };

  return (
   <div className={styles.container}>
    <table className={styles.table}>
      <thead>
      <tr className={styles.row}>
        <th>&nbsp;</th>
        <th>
            <div className={styles.itemName}>
                {t('customer.dashboard.serviceInformation.questionnaireTable.header.name')}
            </div>
        </th>
        <th>{t('customer.dashboard.serviceInformation.questionnaireTable.header.filled')}</th>
        <th>{t('customer.dashboard.serviceInformation.questionnaireTable.header.expires')}</th>
        <th>{t('customer.dashboard.serviceInformation.questionnaireTable.header.actions')}</th>
      </tr>
      </thead>
      <tbody>
      {questionnaires.map(questionnaire => (
        <tr key={uuid()} className={styles.row}>
          <td>
            {isChecked(questionnaire, props.customerQuestionnairesRequired)}
          </td>
          <td>
            <div className={styles.itemName}>
              {getQuestionnaireName(questionnaire)}
            </div>
          </td>
          <td>
            {showCreateTimestamp(questionnaire)}
          </td>
          <td>
            {showExpirationDate(questionnaire)}
          </td>
          <td>
            {(isFilledQuestionnairesHaveType(questionnaire) || showHistory) &&
              <div
                role='button'
                tabIndex={0}
                onClick={() => setShowOptionsType(questionnaire)}
                onKeyDown={() => setShowOptionsType(questionnaire)}
                className={styles.options}
              >
                <img
                  src={threeDotsLogo}
                  alt={t('customer.dashboard.representativeInformation.threeDotsLogo')}
                  className={styles.logo}
                />
              </div>
            }
            {showOptionsType === questionnaire && (
              <OutsideClickHandler
                onOutsideClick={() => setShowOptionsType(undefined)}
              >
                <div className={styles.optionsList}>
                  {findFilledQuestionnaireType(questionnaire) && (
                    <div
                      role='button'
                      tabIndex={0}
                      className={styles.option}
                      onClick={() => handleViewClick(questionnaire)}
                      onKeyDown={() => handleViewClick(questionnaire)}
                    >
                      <span className={styles.optionText}>
                        {t('customer.dashboard.serviceInformation.questionnaireTable.options.last')}
                      </span>
                    </div>
                  )}
                  {showHistory && (
                    <div
                      role='button'
                      tabIndex={0}
                      className={styles.option}
                      onClick={() => handleHistoryClick()}
                      onKeyDown={() => handleHistoryClick()}
                    >
                      <span className={styles.optionText}>
                        {t(
                          'customer.dashboard.serviceInformation.questionnaireTable.options.history'
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </OutsideClickHandler>
            )}
          </td>
        </tr>),
      )}
      </tbody>
    </table>
   </div>
  );
}

export default QuestionnaireTable;
